import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Building2, Plus, Pencil, Trash2, Star, X, Check } from 'lucide-react';

const EMPTY = {
  company_name: '', bin_iin: '', legal_address: '', contact_person: '',
  phone: '', bank_name: '', bank_bik: '', bank_account: '', is_default: false,
};

const FIELDS = [
  ['company_name', 'Название юрлица', 'ТОО «Компания»'],
  ['bin_iin', 'БИН/ИИН (12 цифр)', '181140004678'],
  ['legal_address', 'Юридический адрес', 'г. Алматы, ул. ...'],
  ['contact_person', 'Контактное лицо', 'Эльмира'],
  ['phone', 'Телефон', '+7 701 000 00 00'],
  ['bank_name', 'Банк', 'АО Forte Bank'],
  ['bank_bik', 'БИК', 'IRTYKZKA'],
  ['bank_account', 'IBAN / счёт', 'KZ7496503F0008096898'],
];

export const ClientLegalEntities = ({ clientId }) => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/clients/${clientId}/legal-entities`, { headers: getAuthHeaders() });
      setEntities(res.data || []);
    } catch {
      toast.error('Не удалось загрузить юрлица');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (e) => {
    setEditingId(e.id);
    setForm({ ...EMPTY, ...e });
  };

  const save = async () => {
    try {
      if (editingId === 'new') {
        await axios.post(`${API}/admin/clients/${clientId}/legal-entities`, form, { headers: getAuthHeaders() });
      } else {
        await axios.put(`${API}/admin/legal-entities/${editingId}`, form, { headers: getAuthHeaders() });
      }
      toast.success('Юрлицо сохранено');
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Удалить юрлицо?')) return;
    try {
      await axios.delete(`${API}/admin/legal-entities/${id}`, { headers: getAuthHeaders() });
      toast.success('Юрлицо удалено');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const makeDefault = async (id) => {
    try {
      await axios.put(`${API}/admin/legal-entities/${id}`, { is_default: true }, { headers: getAuthHeaders() });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  return (
    <div className="border rounded-lg p-3 bg-slate-50" data-testid="client-legal-entities">
      <div className="flex items-center justify-between mb-2">
        <Label className="flex items-center gap-2 font-medium">
          <Building2 className="w-4 h-4" /> Юридические лица
        </Label>
        {editingId === null && (
          <Button type="button" size="sm" variant="outline" onClick={() => { setEditingId('new'); setForm(EMPTY); }}
            data-testid="add-legal-entity-btn">
            <Plus className="w-3 h-3 mr-1" /> Добавить
          </Button>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Эти данные уходят в 1С и печатаются в накладной и счёте. Поле «БИН/ИИН» выше — только для поиска.
      </p>

      {loading ? (
        <p className="text-sm text-gray-500">Загрузка…</p>
      ) : (
        <div className="space-y-2">
          {entities.length === 0 && editingId === null && (
            <p className="text-sm text-gray-500" data-testid="legal-entities-empty">Юрлиц нет</p>
          )}
          {entities.map((e) => (
            editingId === e.id ? (
              <EntityForm key={e.id} form={form} setForm={setForm} onSave={save} onCancel={() => setEditingId(null)} />
            ) : (
              <div key={e.id} className="flex items-start justify-between gap-2 bg-white border rounded-md p-2"
                data-testid={`legal-entity-${e.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{e.company_name}</span>
                    {e.is_default && <Badge className="bg-emerald-600 text-xs">основное</Badge>}
                  </div>
                  <p className="text-xs text-gray-600">БИН/ИИН: {e.bin_iin || '—'}</p>
                  {e.bank_account && <p className="text-xs text-gray-500 truncate">{e.bank_name} · {e.bank_account}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  {!e.is_default && (
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" title="Сделать основным"
                      onClick={() => makeDefault(e.id)} data-testid={`legal-entity-default-${e.id}`}>
                      <Star className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button type="button" size="icon" variant="ghost" className="h-7 w-7" title="Изменить"
                    onClick={() => startEdit(e)} data-testid={`legal-entity-edit-${e.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-600" title="Удалить"
                    onClick={() => remove(e.id)} data-testid={`legal-entity-delete-${e.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )
          ))}
          {editingId === 'new' && (
            <EntityForm form={form} setForm={setForm} onSave={save} onCancel={() => setEditingId(null)} isNew />
          )}
        </div>
      )}
    </div>
  );
};

const EntityForm = ({ form, setForm, onSave, onCancel, isNew }) => (
  <div className="bg-white border rounded-md p-3 space-y-2" data-testid="legal-entity-form">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {FIELDS.map(([key, label, placeholder]) => (
        <div key={key}>
          <Label className="text-xs">{label}</Label>
          <Input
            value={form[key] || ''}
            placeholder={placeholder}
            onChange={(ev) => setForm((p) => ({ ...p, [key]: ev.target.value }))}
            className="h-8 text-sm"
            data-testid={`legal-entity-input-${key}`}
          />
        </div>
      ))}
    </div>
    <label className="flex items-center gap-2 text-xs text-gray-700">
      <input type="checkbox" checked={!!form.is_default}
        onChange={(ev) => setForm((p) => ({ ...p, is_default: ev.target.checked }))}
        data-testid="legal-entity-input-is_default" />
      Основное юрлицо (подставляется в заказ по умолчанию)
    </label>
    <div className="flex gap-2 pt-1">
      <Button type="button" size="sm" onClick={onSave} className="bg-emerald-700 hover:bg-emerald-800"
        data-testid="legal-entity-save-btn">
        <Check className="w-3.5 h-3.5 mr-1" /> {isNew ? 'Создать' : 'Сохранить'}
      </Button>
      <Button type="button" size="sm" variant="outline" onClick={onCancel} data-testid="legal-entity-cancel-btn">
        <X className="w-3.5 h-3.5 mr-1" /> Отмена
      </Button>
    </div>
  </div>
);
